import { create } from 'zustand';
import {
  fetchBoard,
  patchBoardPlacementOps,
  patchBoardViewport,
  patchBoardSnapshot,
  patchBoardOps,
  patchBoardFrameOps,
  patchBoardDescription,
  patchBoardLocale,
  patchPinEpistemicType,
  patchYouTubeMoment,
  deleteActivityById,
} from '../api/boardApi';
import {
  readDefaultGeminiLanguage,
  resolveSeedGeminiLanguage,
} from '../utils/geminiLanguageStorage';
import { mergeBoardState, getActivityId } from '../utils/autoLayout';
import {
  generateBoardSteps,
  patchBoardSteps,
  fillBoardStep,
  setBoardCurrentStep,
  updateBoardStep,
  deleteBoardStep,
  patchBoardStepConstraints,
} from '../api/stepsApi';
import {
  applyRecsToBoard,
  currentStep,
  findStep,
  pickStepsPayload,
  projectStepsToRecs,
  digestAfterUserProgressWrite,
} from '../utils/steps';
import {
  readBoardGoal,
  readCurrentStep,
  normalizeBoardGoalFields,
  boardWithSavedGoal,
} from '../utils/boardGoalFields';
import { isPendingUrlActivityId } from '../utils/pendingUrlPin';
import {
  readYouTubeMomentSeconds,
  withYouTubeMoment,
  withYouTubeMomentImage,
  withoutYouTubeMomentImage,
} from '../utils/youtubeUrl';
import { normalizeProcessingTargets } from '../utils/objectProcessing';
import { blockerDoneKey, normalizeGoalText } from '../utils/goalHistory';
import { editContextMemory as applyContextMemoryEdits } from '../utils/contextMemory';
import {
  preferRicherAnnotationSnapshot,
  mergePreferLocalPlacements,
  shouldPersistPlacements,
} from '../utils/boardSnapshotGuards';
import { buildPlacementOps } from '../utils/placementOps';
import { buildFrameOps } from '../utils/frameOps';
import {
  quantizeViewport,
  viewportsEqual,
} from '../utils/boardViewportPersist';
import {
  foldBoardIntentSummary,
  INTENT_EVENT_TYPES,
  pushBoardIntentEvent,
} from '../utils/boardRecentIntent';

let boardLoadSeq = 0;
/** Bumped on each board-goal save so an in-flight load cannot restore a stale goal. */
let boardGoalWriteSeq = 0;
/**
 * Queue for heavy snapshot/document-ops writes (shared canvasRevision).
 * Placements/frames use their own chains so pin moves are not stuck behind
 * multi-second snapshot PATCHes.
 */
let canvasRevisionWriteChain = Promise.resolve();
/** Serialize placement PATCHes; each job writes the latest local placements. */
let placementSaveChain = Promise.resolve();
/** Serialize frame PATCHes; each job writes the latest local frames. */
let frameSaveChain = Promise.resolve();
/** Serialize viewport PATCHes; each job writes the latest local viewport. */
let viewportSaveChain = Promise.resolve();
/** Last viewport successfully persisted to the server (per tab). */
let viewportLastPersisted = null;
/** Last placements acknowledged by the server (for rebuilding placement ops). */
let placementsLastAcked = null;
/** Last frames acknowledged by the server (for rebuilding frame ops). */
let framesLastAcked = null;
/** Per-pin generation so older pin-epistemic PATCH responses cannot overwrite newer clicks. */
const pinEpistemicUpdateSeq = new Map();
const REVISION_CONFLICT_RETRIES = 3;

function extractRevisionFromResponse(data, fallbackBoard) {
  const explicit = Number(data?.revision);
  if (Number.isFinite(explicit)) return explicit;
  const fromBoard = Number(data?.board?.canvasRevision ?? fallbackBoard?.canvasRevision);
  if (Number.isFinite(fromBoard)) return fromBoard;
  return null;
}

function isRevisionConflict(err) {
  return Boolean(err && (err.status === 409 || err.body?.code === 'BOARD_REVISION_CONFLICT'));
}

function applyConflictRevision(err) {
  const revision = extractRevisionFromResponse(err?.body, err?.body?.board);
  if (Number.isFinite(revision)) {
    useBoardStore.getState().updateCanvasRevisionLocal(revision);
  }
  return revision;
}

function enqueueCanvasRevisionWrite(job) {
  const run = canvasRevisionWriteChain.catch(() => {}).then(job);
  canvasRevisionWriteChain = run.catch(() => {});
  return run;
}

function enqueuePlacementWrite(job) {
  const run = placementSaveChain.catch(() => {}).then(job);
  placementSaveChain = run.catch(() => {});
  return run;
}

function enqueueFrameWrite(job) {
  const run = frameSaveChain.catch(() => {}).then(job);
  frameSaveChain = run.catch(() => {});
  return run;
}

async function withRevisionRetry(write) {
  let lastErr = null;
  for (let attempt = 0; attempt < REVISION_CONFLICT_RETRIES; attempt += 1) {
    try {
      return await write();
    } catch (err) {
      lastErr = err;
      if (!isRevisionConflict(err)) throw err;
      applyConflictRevision(err);
    }
  }
  throw lastErr;
}

export const useBoardStore = create((set, get) => ({
  board: null,
  activities: [],
  isOwner: false,
  canEdit: false,
  role: 'viewer',
  loading: false,
  error: null,
  errorCode: null,
  selectedActivityId: null,
  focusedPinId: null,
  youtubePlayback: null,
  personName: null,
  hashtag: null,
  /** Bumped when a non-Activity object (text/image/AI pin) changes knowledge type. */
  canvasEpistemicNonce: 0,
  /** Bumped after a board-goal Run is stored, so Do-next rewrites its steps. */
  boardGoalRefreshToken: 0,
  /** Ephemeral post-add flow (done fill, location, enrich) shown above objects. */
  objectProcessing: {},

  /**
   * Seed an empty editable board so the canvas can mount immediately after create,
   * before the network loadBoard refresh finishes.
   */
  prepareEmptyBoard: (personName, hashtag, { description = '' } = {}) => {
    if (!personName || !hashtag) return;
    const nextDescription = String(description || '').slice(0, 500);
    set({
      loading: false,
      error: null,
      errorCode: null,
      isOwner: true,
      canEdit: true,
      role: 'owner',
      personName,
      hashtag,
      activities: [],
      selectedActivityId: null,
      focusedPinId: null,
      youtubePlayback: null,
      canvasEpistemicNonce: 0,
      objectProcessing: {},
      board: {
        currentStep: '',
        boardGoal: nextDescription,
        placements: [],
        frames: [],
        tldrawSnapshot: null,
        viewport: null,
        contextRecommendations: null,
        locale: readDefaultGeminiLanguage(),
      },
    });
  },

  loadBoard: async (personName, hashtag, options = {}) => {
    const store = get();
    if (
      !options.force &&
      store.personName === personName &&
      store.hashtag === hashtag &&
      store.activities.length > 0
    ) {
      return;
    }
    const loadSeq = ++boardLoadSeq;
    const goalSeqAtStart = boardGoalWriteSeq;
    const sameBoardAlreadyLoaded =
      store.personName === personName &&
      store.hashtag === hashtag &&
      store.board != null;
    // Keep current board visible during forced refresh (e.g. socket pin sync).
    if (sameBoardAlreadyLoaded && options.force) {
      set({ error: null, errorCode: null });
    } else {
      set({
        loading: true,
        error: null,
        errorCode: null,
        isOwner: false,
        canEdit: false,
        role: 'viewer',
        personName,
        hashtag,
        activities: [],
        board: null,
        youtubePlayback: null,
        canvasEpistemicNonce: 0,
        objectProcessing: {},
      });
    }
    try {
      const data = await fetchBoard(personName, hashtag, {
        locale: readDefaultGeminiLanguage(),
      });
      if (loadSeq !== boardLoadSeq) return;
      let mergedBoard = normalizeBoardGoalFields(
        mergeBoardState(data.activities || [], data.board || {})
      );
      let writeBackSnapshot = null;
      let writeBackPlacements = null;
      // Force refresh can race an in-flight snapshot/placements PATCH. Keep the
      // richer local state when the server response is emptier/stale.
      if (sameBoardAlreadyLoaded) {
        const localBoard = get().board;
        const localSnapshot = localBoard?.tldrawSnapshot;
        const preferred = preferRicherAnnotationSnapshot(
          localSnapshot,
          mergedBoard.tldrawSnapshot
        );
        if (preferred && preferred !== mergedBoard.tldrawSnapshot) {
          mergedBoard = { ...mergedBoard, tldrawSnapshot: preferred };
          writeBackSnapshot = preferred;
        }
        const localFrames = localBoard?.frames;
        if (
          Array.isArray(localFrames)
          && localFrames.length > (mergedBoard.frames || []).length
        ) {
          mergedBoard = { ...mergedBoard, frames: localFrames };
        }
        const preferredPlacements = mergePreferLocalPlacements(
          localBoard?.placements,
          mergedBoard.placements
        );
        if (
          preferredPlacements
          && JSON.stringify(preferredPlacements) !== JSON.stringify(mergedBoard.placements || [])
        ) {
          mergedBoard = { ...mergedBoard, placements: preferredPlacements };
          writeBackPlacements = preferredPlacements;
        }
        const localGoal = readBoardGoal(localBoard);
        if (localGoal.trim() && !readBoardGoal(mergedBoard).trim()) {
          mergedBoard = { ...mergedBoard, boardGoal: localGoal };
        }
        const localStep = readCurrentStep(localBoard);
        if (localStep.trim() && !readCurrentStep(mergedBoard).trim()) {
          mergedBoard = { ...mergedBoard, currentStep: localStep };
        }
      }
      const canEdit = Boolean(data.canEdit ?? data.isOwner);
      // Existing boards created before per-canvas locale: seed once without following
      // later "default for new canvases" changes.
      if (!mergedBoard.locale) {
        const seededLocale = resolveSeedGeminiLanguage(mergedBoard);
        mergedBoard = { ...mergedBoard, locale: seededLocale };
        if (canEdit) {
          patchBoardLocale(hashtag, seededLocale).catch(err => {
            console.warn('Failed to seed board locale', err);
          });
        }
      }
      // Keep in-flight optimistic URL paste pins across force refresh races.
      const prior = get();
      const serverActivities = data.activities || [];
      const serverUrls = new Set(
        serverActivities
          .map(a => String(a?.content?.[0]?.urls?.[0]?.href || a?.content?.[0]?.url || '').trim())
          .filter(Boolean)
      );
      const pendingActivities = (prior.activities || []).filter(activity => {
        if (!activity?.pendingUrlLoad && !isPendingUrlActivityId(getActivityId(activity))) {
          return false;
        }
        const pendingUrl = String(
          activity.pendingUrl || activity.content?.[0]?.urls?.[0]?.href || ''
        ).trim();
        return !pendingUrl || !serverUrls.has(pendingUrl);
      });
      const pendingIds = new Set(pendingActivities.map(getActivityId));
      const pendingPlacements = (prior.board?.placements || []).filter(p =>
        pendingIds.has(String(p.activityId))
      );
      if (pendingPlacements.length) {
        mergedBoard = {
          ...mergedBoard,
          placements: [...(mergedBoard.placements || []), ...pendingPlacements],
        };
      }

      // A Run that landed during this fetch is newer than the response.
      if (goalSeqAtStart !== boardGoalWriteSeq) {
        const localGoal = readBoardGoal(get().board);
        mergedBoard = { ...mergedBoard, boardGoal: localGoal };
        delete mergedBoard.description;
      }

      set({
        board: mergedBoard,
        activities: [...pendingActivities, ...serverActivities],
        isOwner: Boolean(data.isOwner),
        canEdit,
        role: data.role || (data.isOwner ? 'owner' : 'viewer'),
        personName,
        hashtag,
        loading: false,
      });
      viewportLastPersisted = quantizeViewport(mergedBoard.viewport);
      // Never ack optimistic pending-url placements — they are local-only.
      placementsLastAcked = (Array.isArray(mergedBoard.placements)
        ? mergedBoard.placements
        : []
      ).filter(p => !isPendingUrlActivityId(p?.activityId));
      framesLastAcked = Array.isArray(mergedBoard.frames) ? mergedBoard.frames : [];
      // Persist the richer local state so the next cold refresh stays correct.
      if (canEdit && writeBackSnapshot) {
        enqueueCanvasRevisionWrite(async () => {
          try {
            await withRevisionRetry(async () => {
              const latest = get().board?.tldrawSnapshot;
              if (!latest || get().hashtag !== hashtag) return;
              const data = await patchBoardSnapshot(hashtag, latest, {
                baseRevision: Number(get().board?.canvasRevision ?? 0),
              });
              const revision = extractRevisionFromResponse(data, get().board);
              if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
            });
          } catch (err) {
            console.warn('Failed to write back richer board snapshot', err);
          }
        });
      }
      if (canEdit && writeBackPlacements) {
        enqueuePlacementWrite(async () => {
          try {
            await withRevisionRetry(async () => {
              const latest = get().board?.placements;
              if (!Array.isArray(latest) || get().hashtag !== hashtag) return;
              const baseline = Array.isArray(placementsLastAcked) ? placementsLastAcked : [];
              const ops = buildPlacementOps(baseline, latest);
              if (!ops.length) return;
              const data = await patchBoardPlacementOps(hashtag, ops, {
                baseRevision: Number(get().board?.canvasRevision ?? 0),
              });
              const revision = extractRevisionFromResponse(data, get().board);
              if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
              placementsLastAcked = latest;
            });
          } catch (err) {
            console.warn('Failed to write back richer board placements', err);
          }
        });
      }
    } catch (err) {
      if (loadSeq !== boardLoadSeq) return;
      set({
        loading: false,
        error: err.message || 'Failed to load board',
        errorCode: err.code || null,
      });
    }
  },

  setSelectedActivityId: id => set({ selectedActivityId: id || null }),
  setFocusedPinId: id => set({ focusedPinId: id ? String(id) : null }),
  openYoutubePlayback: video => set({
    youtubePlayback: video?.videoId ? video : null,
  }),
  closeYoutubePlayback: () => set({ youtubePlayback: null }),

  saveYoutubeMoment: async (activityId, seconds, imageDataUrl) => {
    const saved = Math.floor(Number(seconds));
    if (!(saved >= 1) || saved > 86400) return;
    const { hashtag, canEdit, isOwner, activities, updateActivityLocal } = get();
    if (!hashtag || !(canEdit || isOwner)) return;
    const id = String(activityId || '');
    if (!id || isPendingUrlActivityId(id)) return;
    const activity = activities.find(item => getActivityId(item) === id);
    if (!activity) return;
    const shot = String(imageDataUrl || '').trim();
    const secondsChanged = readYouTubeMomentSeconds(activity) !== saved;
    if (!secondsChanged && !shot) return;
    let preview = shot
      ? withYouTubeMomentImage(activity, saved, shot)
      : withYouTubeMoment(activity, saved);
    if (!shot && secondsChanged) preview = withoutYouTubeMomentImage(preview);
    updateActivityLocal(id, { action: preview.action, content: preview.content });
    try {
      const result = await patchYouTubeMoment(hashtag, id, saved, shot || undefined);
      const current = get().activities.find(item => getActivityId(item) === id);
      if (!current) return;
      const storedUrl = result?.elementScreenshotThumbUrl || result?.elementScreenshotUrl;
      const stored = storedUrl
        ? withYouTubeMomentImage(current, saved, storedUrl)
        : (result?.momentImageCleared ? withoutYouTubeMomentImage(current) : null);
      if (!stored) return;
      updateActivityLocal(id, { action: stored.action, content: stored.content });
    } catch (err) {
      console.warn('Failed to save YouTube moment', err);
    }
  },

  clearBoard: () => {
    viewportLastPersisted = null;
    placementsLastAcked = null;
    framesLastAcked = null;
    set({
      board: null,
      activities: [],
      isOwner: false,
      canEdit: false,
      role: 'viewer',
      loading: false,
      error: null,
      errorCode: null,
      selectedActivityId: null,
      focusedPinId: null,
      youtubePlayback: null,
      personName: null,
      hashtag: null,
    });
  },

  updatePlacementsLocal: placements =>
    set(state => ({
      board: state.board ? { ...state.board, placements } : { placements },
    })),

  updateSnapshotLocal: tldrawSnapshot =>
    set(state => ({
      board: state.board ? { ...state.board, tldrawSnapshot } : { tldrawSnapshot },
    })),

  updateFramesLocal: frames =>
    set(state => ({
      board: state.board ? { ...state.board, frames } : { frames },
    })),

  updateCanvasRevisionLocal: revision =>
    set(state => {
      const next = Number(revision);
      if (!Number.isFinite(next)) return state;
      const current = Number(state.board?.canvasRevision ?? 0);
      if (next < current) return state;
      return {
        board: state.board ? { ...state.board, canvasRevision: next } : { canvasRevision: next },
      };
    }),

  // ---------------------------------------------------------------------------
  // Steps v2 — `board.steps[]` + `board.currentStepId` are the source of truth.
  // Every server reply carries the steps payload plus the legacy projections
  // (currentStep / contextRecommendations / goalHistory) so readers of those
  // fields keep working without any client-side merge logic.
  // ---------------------------------------------------------------------------

  /** Apply a steps payload from any `/api/board/steps/*` reply. */
  applyStepsPayload: (data, { hashtag: forHashtag = null } = {}) => {
    const payload = pickStepsPayload(data);
    if (!payload) return null;
    if (forHashtag && get().hashtag !== forHashtag) return null;
    set(state => ({
      board: state.board ? { ...state.board, ...payload } : { ...payload },
    }));
    return payload;
  },

  updateGoalLocal: goal =>
    set(state => ({
      board: state.board ? { ...state.board, currentStep: goal } : { currentStep: goal },
    })),

  /** Local-only; no-op for persistence (kept for callers that mark a goal as "ran"). */
  recordRanGoalLocal: goal =>
    set(state => {
      const normalized = normalizeGoalText(goal);
      if (!normalized || !state.board) return state;
      return { board: { ...state.board, currentStep: normalized } };
    }),

  updateDescriptionLocal: description =>
    set(state => ({
      board: state.board
        ? { ...state.board, boardGoal: description }
        : { boardGoal: description },
    })),

  updateLocaleLocal: locale =>
    set(state => ({
      board: state.board ? { ...state.board, locale } : { locale },
    })),

  /**
   * Local recs update. Step-scoped fields (chips, progress, %, summary) are
   * written onto the current step in `board.steps[]` and `contextRecommendations`
   * is re-projected from the steps, so the dropdown and the blocker section
   * never diverge.
   */
  updateContextRecommendationsLocal: contextRecommendations =>
    set(state => ({
      board: applyRecsToBoard(state.board, contextRecommendations),
    })),

  updateRecentIntentLocal: recentIntent =>
    set(state => ({
      board: state.board
        ? { ...state.board, recentIntent: String(recentIntent || '') }
        : { recentIntent: String(recentIntent || '') },
    })),

  /**
   * Persist the parts of contextRecommendations the client owns: the
   * constraints ledger and pending digest. Coach chips / progress are owned by
   * steps and written through the step actions below.
   */
  persistContextRecommendations: async contextRecommendations => {
    const { hashtag, isOwner } = get();
    if (!hashtag || !isOwner || !contextRecommendations) return null;
    set(state => ({
      board: applyRecsToBoard(state.board, contextRecommendations),
    }));
    try {
      const result = await patchBoardStepConstraints(hashtag, {
        constraints: contextRecommendations.constraints,
        pendingDigest: contextRecommendations.pendingDigest,
      });
      if (get().hashtag !== hashtag) return contextRecommendations;
      set(state => ({
        board: state.board
          ? {
            ...state.board,
            contextRecommendations: {
              ...(state.board.contextRecommendations || {}),
              ...(Array.isArray(result?.constraints) ? { constraints: result.constraints } : {}),
              ...(result?.pendingDigest ? { pendingDigest: result.pendingDigest } : {}),
            },
          }
          : state.board,
      }));
    } catch (err) {
      console.warn('Failed to persist context constraints', err?.message || err);
    }
    return get().board?.contextRecommendations || contextRecommendations;
  },

  /** Board-goal Run → full-canvas coach rewrites the open steps. */
  generateSteps: async clientContext => {
    const { hashtag, isOwner } = get();
    if (!hashtag || !isOwner) return null;
    const data = await generateBoardSteps(hashtag, clientContext);
    get().applyStepsPayload(data, { hashtag });
    return data;
  },

  /** Refresh → whole new open list (ledger + digest, no canvas scene). */
  patchSteps: async clientContext => {
    const { hashtag, isOwner } = get();
    if (!hashtag || !isOwner) return null;
    const data = await patchBoardSteps(hashtag, clientContext);
    get().applyStepsPayload(data, { hashtag });
    return data;
  },

  /** Chips for one step that has no pack yet. */
  fillStep: async (stepId, clientContext, { force = false } = {}) => {
    const { hashtag, isOwner } = get();
    if (!hashtag || !isOwner || !stepId) return null;
    const data = await fillBoardStep(hashtag, stepId, clientContext, { force });
    get().applyStepsPayload(data, { hashtag });
    return data;
  },

  /** Point at an existing step (pin-case match, plan option). Steps are never created here. */
  setCurrentStep: async ({ stepId = '' } = {}) => {
    const { hashtag, isOwner, canEdit, board } = get();
    if (!hashtag || !(isOwner || canEdit)) return null;
    const target = findStep(board, stepId);
    if (!target) return null;
    set(state => ({
      board: state.board
        ? { ...state.board, currentStepId: target.id, currentStep: target.title }
        : state.board,
    }));
    try {
      const data = await setBoardCurrentStep(hashtag, target.id);
      get().applyStepsPayload(data, { hashtag });
      return data;
    } catch (err) {
      console.warn('Failed to set current step', err?.message || err);
      return null;
    }
  },

  /** Status / progress / rate for one step. */
  updateStep: async (stepId, patch = {}) => {
    const { hashtag, isOwner } = get();
    if (!hashtag || !isOwner || !stepId) return null;
    try {
      const data = await updateBoardStep(hashtag, stepId, patch);
      get().applyStepsPayload(data, { hashtag });
      return data;
    } catch (err) {
      console.warn('Failed to update step', err?.message || err);
      return null;
    }
  },

  /**
   * Persist the current step's gap progress (ticks / % / tried destinations).
   * Status 'finished' closes the step and the server advances the pointer.
   *
   * The server unions `progress` into the stored record, so a plain write can
   * only add keys. Undo (Solved / Not interested → off) passes `removeDoneKeys`;
   * we then send `progressReplace` built from the stored record with the key
   * lists overwritten and any pin-case fills for those gaps dropped, otherwise
   * hydration would re-mark the gap done from `doneFills`.
   */
  persistCurrentGoalProgress: async ({
    achievementRate,
    doneBlockerKeys = [],
    doneValidationKeys = [],
    notInterestedKeys = [],
    triedActionKeys = [],
    aiAchievementRate = null,
    status: statusOverride = null,
    removeDoneKeys = [],
  } = {}) => {
    const { board } = get();
    const step = currentStep(board);
    if (!step) return null;
    const rate = Number.isFinite(Number(achievementRate))
      ? Math.max(0, Math.min(100, Math.round(Number(achievementRate))))
      : null;
    const progress = {
      doneBlockerKeys,
      doneValidationKeys,
      notInterestedKeys,
      triedActionKeys,
      ...(Number.isFinite(Number(aiAchievementRate)) ? { aiAchievementRate } : {}),
    };
    const dropKeys = new Set(
      (Array.isArray(removeDoneKeys) ? removeDoneKeys : [])
        .map(key => String(key || '').trim().toLowerCase())
        .filter(Boolean)
    );
    const replace = dropKeys.size > 0;
    const buildNextProgress = (prevProgress) => {
      const prev = prevProgress && typeof prevProgress === 'object' ? prevProgress : {};
      const next = { ...prev, ...progress };
      if (!replace) return next;
      const matches = entry => dropKeys.has(String(entry?.doneKey || '').trim().toLowerCase());
      next.doneFills = (Array.isArray(prev.doneFills) ? prev.doneFills : [])
        .filter(entry => !matches(entry));
      return next;
    };
    const finished = statusOverride === 'finished';
    // Undo on a step the ticks had already closed: gaps are the truth, so the
    // step itself goes back to open (and stays current — no pointer advance).
    const reopen = !finished && step.status === 'finished';
    // Optimistic local progress so chips do not flicker while the PATCH is in
    // flight. Written on the step itself; contextRecommendations is re-projected.
    set(state => {
      if (!state.board || !Array.isArray(state.board.steps)) return state;
      const priorSteps = state.board.steps;
      const steps = priorSteps.map(s => (
        s?.id === step.id
          ? {
            ...s,
            ...(reopen ? { status: 'open', finishedBy: null } : {}),
            ...(finished
              ? { status: 'finished', finishedBy: s.status === 'finished' ? s.finishedBy : 'user' }
              : {}),
            ...(rate != null ? { achievementRate: rate } : {}),
            progress: buildNextProgress(s.progress),
          }
          : s
      ));
      const withSteps = { ...state.board, steps };
      const recs = projectStepsToRecs(withSteps);
      return {
        board: {
          ...withSteps,
          contextRecommendations: {
            ...recs,
            pendingDigest: digestAfterUserProgressWrite(recs.pendingDigest, {
              priorSteps,
              nextSteps: steps,
            }),
          },
        },
      };
    });
    let progressPatch = { progress };
    if (replace) {
      const progressReplace = buildNextProgress(findStep(get().board, step.id)?.progress);
      progressPatch = { progressReplace };
    }
    return get().updateStep(step.id, {
      ...progressPatch,
      ...(rate != null ? { achievementRate: rate } : {}),
      ...(finished ? { status: 'finished' } : {}),
      ...(reopen ? { status: 'open', advance: false } : {}),
    });
  },

  /** Hide a step (thumbs-down). Marks its gaps not-interested when it is current. */
  markGoalOptionNotInterested: async (goal, { blockers = [] } = {}) => {
    const { board } = get();
    const step = findStep(board, goal);
    if (!step) return null;
    const gapKeys = (Array.isArray(blockers) ? blockers : [])
      .map((blocker, index) => blockerDoneKey(blocker, index))
      .map(key => String(key || '').trim())
      .filter(Boolean);
    return get().updateStep(step.id, {
      status: 'not_interested',
      ...(gapKeys.length ? { progress: { notInterestedKeys: gapKeys } } : {}),
    });
  },

  /** Settings: un-hide a step (not-interested or finished → open). */
  restoreNotInterestedGoalOption: async goal => {
    const { hashtag, board } = get();
    const step = findStep(board, goal);
    if (!step) return null;
    pushBoardIntentEvent(hashtag, {
      type: INTENT_EVENT_TYPES.GAP_INTERESTED,
      label: step.title,
    });
    get().updateRecentIntentLocal(foldBoardIntentSummary(hashtag, {
      priorSummary: board?.recentIntent || '',
    }));
    return get().updateStep(step.id, { status: 'open', advance: false });
  },

  /** Settings: delete a step for good. */
  permanentlyDeleteNotInterestedGoalOption: async goal => {
    const { hashtag, isOwner, board } = get();
    if (!hashtag || !isOwner) return null;
    const step = findStep(board, goal);
    if (!step) return null;
    pushBoardIntentEvent(hashtag, {
      type: INTENT_EVENT_TYPES.STEP_DROPPED,
      label: step.title,
    });
    get().updateRecentIntentLocal(foldBoardIntentSummary(hashtag, {
      priorSummary: board?.recentIntent || '',
    }));
    try {
      const data = await deleteBoardStep(hashtag, step.id);
      get().applyStepsPayload(data, { hashtag });
      return data;
    } catch (err) {
      console.warn('Failed to delete step', err?.message || err);
      return null;
    }
  },

  /**
   * Settings: restore not-interested / solved marks on the current step and
   * delete or toggle constraints. Progress is replaced (keys removed), the
   * ledger is written as-is.
   */
  editContextMemory: async (edits = {}) => {
    const { hashtag, isOwner, board } = get();
    if (!hashtag || !isOwner) return null;
    const prevRecs = board?.contextRecommendations && typeof board.contextRecommendations === 'object'
      ? board.contextRecommendations
      : {};
    const edited = applyContextMemoryEdits(prevRecs, edits);
    if (!edited) return null;

    const restoredGaps = (Array.isArray(edits.removeNotInterestedKeys)
      ? edits.removeNotInterestedKeys
      : [])
      .map(item => String(item || '').trim())
      .filter(Boolean);
    for (const label of restoredGaps) {
      pushBoardIntentEvent(hashtag, { type: INTENT_EVENT_TYPES.GAP_INTERESTED, label });
    }
    const nextIntent = restoredGaps.length
      ? foldBoardIntentSummary(hashtag, { priorSummary: board?.recentIntent || '' })
      : (board?.recentIntent || '');

    set(state => ({
      board: { ...applyRecsToBoard(state.board, edited), recentIntent: nextIntent },
    }));

    const progressChanged = JSON.stringify(prevRecs.userProgress || {})
      !== JSON.stringify(edited.userProgress || {});
    const constraintsChanged = JSON.stringify(prevRecs.constraints || [])
      !== JSON.stringify(edited.constraints || []);
    try {
      const step = currentStep(board);
      if (progressChanged && step) {
        const { notInterestedGoalKeys: _omit, ...progressReplace } = edited.userProgress || {};
        await get().updateStep(step.id, { progressReplace, advance: false });
      }
      if (constraintsChanged) {
        await patchBoardStepConstraints(hashtag, { constraints: edited.constraints });
      }
    } catch (err) {
      console.warn('Failed to update canvas context memory', err);
      throw err;
    }
    return get().board?.contextRecommendations || edited;
  },

  saveDescription: async description => {
    const { hashtag, isOwner, board } = get();
    if (!hashtag || !isOwner) return false;
    const normalized = String(description || '').slice(0, 500);
    if (readBoardGoal(board) === normalized) return false;
    const prevBoardGoal = readBoardGoal(board);
    const writeSeq = ++boardGoalWriteSeq;
    set(state => ({
      board: boardWithSavedGoal(state.board, normalized),
    }));
    try {
      const data = await patchBoardDescription(hashtag, normalized);
      if (writeSeq !== boardGoalWriteSeq || get().hashtag !== hashtag) return true;
      const nextBoard = data?.board ? normalizeBoardGoalFields(data.board) : null;
      const stored = nextBoard ? readBoardGoal(nextBoard) : '';
      if (!nextBoard || stored !== normalized) {
        console.warn('Board goal save did not round-trip', { sent: normalized, stored });
        set(state => ({
          board: boardWithSavedGoal(state.board, prevBoardGoal),
        }));
        return false;
      }
      set(state => ({
        board: boardWithSavedGoal(state.board, stored),
        ...(stored.trim()
          ? { boardGoalRefreshToken: (state.boardGoalRefreshToken || 0) + 1 }
          : {}),
      }));
      return true;
    } catch (err) {
      console.warn('Failed to save board description', err);
      if (writeSeq === boardGoalWriteSeq && get().hashtag === hashtag) {
        set(state => ({
          board: boardWithSavedGoal(state.board, prevBoardGoal),
        }));
      }
      return false;
    }
  },

  saveLocale: async locale => {
    const { hashtag, canEdit, isOwner, board } = get();
    if (!hashtag || !(canEdit || isOwner)) return;
    const normalized = String(locale || '').trim();
    if (!normalized || (board?.locale || '') === normalized) return;
    get().updateLocaleLocal(normalized);
    try {
      await patchBoardLocale(hashtag, normalized);
    } catch (err) {
      console.warn('Failed to save board locale', err);
    }
  },

  saveFrames: async frames => {
    const { hashtag, canEdit, isOwner, board } = get();
    if (!hashtag || !(canEdit || isOwner)) return;
    const nextFrames = Array.isArray(frames) ? frames : [];
    const baseline = Array.isArray(framesLastAcked)
      ? framesLastAcked
      : (board?.frames || []);
    const frameOps = buildFrameOps(baseline, nextFrames);
    if (!frameOps.length) return;
    get().updateFramesLocal(nextFrames);
    return enqueueFrameWrite(async () => {
      try {
        await withRevisionRetry(async () => {
          if (get().hashtag !== hashtag) return;
          const latest = Array.isArray(get().board?.frames) ? get().board.frames : [];
          const ack = Array.isArray(framesLastAcked) ? framesLastAcked : [];
          const ops = buildFrameOps(ack, latest);
          if (!ops.length) {
            framesLastAcked = latest;
            return;
          }
          const data = await patchBoardFrameOps(hashtag, ops, {
            baseRevision: Number(get().board?.canvasRevision ?? 0),
          });
          const revision = extractRevisionFromResponse(data, get().board);
          if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
          framesLastAcked = latest;
        });
      } catch (err) {
        console.warn('Failed to save board frames', err);
      }
    });
  },

  applyAiPlacements: placements => get().savePlacements(placements),

  savePlacements: async placements => {
    const { hashtag, canEdit, isOwner, board } = get();
    if (!hashtag || !(canEdit || isOwner)) return;
    const prev = board?.placements;
    if (!shouldPersistPlacements(placements, prev)) return;
    const incoming = Array.isArray(placements) ? placements : [];
    // Optimistic URL-paste pins are local-only until the server places the real activity.
    const pendingPlacements = incoming.filter(p => isPendingUrlActivityId(p?.activityId));
    const nextPlacements = incoming.filter(p => !isPendingUrlActivityId(p?.activityId));
    const baseline = (Array.isArray(placementsLastAcked) ? placementsLastAcked : (prev || []))
      .filter(p => !isPendingUrlActivityId(p?.activityId));
    const placementOps = buildPlacementOps(baseline, nextPlacements);
    // Keep pending pins on the canvas even when there is nothing to PATCH yet.
    get().updatePlacementsLocal([...nextPlacements, ...pendingPlacements]);
    if (!placementOps.length) return;
    // Own queue — pin moves must not wait behind multi-second snapshot writes.
    return enqueuePlacementWrite(async () => {
      try {
        await withRevisionRetry(async () => {
          if (get().hashtag !== hashtag) return;
          const latest = Array.isArray(get().board?.placements) ? get().board.placements : [];
          const ack = Array.isArray(placementsLastAcked) ? placementsLastAcked : [];
          if (!shouldPersistPlacements(latest, ack)) return;
          const ops = buildPlacementOps(ack, latest);
          if (!ops.length) {
            placementsLastAcked = latest;
            return;
          }
          const data = await patchBoardPlacementOps(hashtag, ops, {
            baseRevision: Number(get().board?.canvasRevision ?? 0),
          });
          const revision = extractRevisionFromResponse(data, get().board);
          if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
          placementsLastAcked = latest;
        });
      } catch (err) {
        console.warn('Failed to save board placements', err);
      }
    });
  },

  saveViewport: viewport => {
    const { hashtag, canEdit, isOwner, board } = get();
    if (!hashtag || !(canEdit || isOwner)) return;
    const next = quantizeViewport(viewport);
    if (!next) return;
    if (viewportsEqual(board?.viewport, next) && viewportsEqual(viewportLastPersisted, next)) {
      return;
    }
    set(state => ({
      board: state.board ? { ...state.board, viewport: next } : { viewport: next },
    }));
    // Queue writes so overlapping debounced saves cannot hammer the server.
    viewportSaveChain = viewportSaveChain
      .catch(() => {})
      .then(async () => {
        const latest = quantizeViewport(get().board?.viewport);
        const activeHashtag = get().hashtag;
        if (!latest || activeHashtag !== hashtag) return;
        if (viewportsEqual(viewportLastPersisted, latest)) return;
        try {
          await patchBoardViewport(activeHashtag, latest);
          viewportLastPersisted = latest;
        } catch (err) {
          console.warn('Failed to save board viewport', err);
        }
      });
    return viewportSaveChain;
  },

  saveSnapshot: async tldrawSnapshot => {
    const { hashtag, canEdit, isOwner } = get();
    if (!hashtag || !(canEdit || isOwner) || !tldrawSnapshot) return;
    get().updateSnapshotLocal(tldrawSnapshot);
    return enqueueCanvasRevisionWrite(async () => {
      try {
        await withRevisionRetry(async () => {
          const latest = get().board?.tldrawSnapshot;
          if (!latest || get().hashtag !== hashtag) return;
          const data = await patchBoardSnapshot(hashtag, latest, {
            baseRevision: Number(get().board?.canvasRevision ?? 0),
          });
          const revision = extractRevisionFromResponse(data, get().board);
          if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
        });
      } catch (err) {
        console.warn('Failed to save board snapshot', err);
      }
    });
  },

  saveDocumentOps: async ops => {
    const { hashtag, canEdit, isOwner } = get();
    if (!hashtag || !(canEdit || isOwner) || !Array.isArray(ops) || !ops.length) return;
    // Coalesce by id: last put wins; remove beats earlier put for same id.
    const byId = new Map();
    for (const op of ops) {
      if (!op?.id) continue;
      byId.set(String(op.id), op);
    }
    const coalesced = [...byId.values()];
    if (!coalesced.length) return;

    return enqueueCanvasRevisionWrite(async () => {
      try {
        await withRevisionRetry(async () => {
          if (get().hashtag !== hashtag) return;
          const data = await patchBoardOps(hashtag, coalesced, {
            baseRevision: Number(get().board?.canvasRevision ?? 0),
          });
          const revision = Number(data?.revision);
          if (Number.isFinite(revision)) get().updateCanvasRevisionLocal(revision);
        });
      } catch (err) {
        console.warn('Failed to save board ops', err);
      }
    });
  },

  prependActivity: activity => {
    if (!activity) return;
    const store = get();
    const key = String(activity._id || activity.actionId || '');
    if (!key || store.activities.some(a => String(a._id || a.actionId) === key)) return;
    set({ activities: [activity, ...store.activities] });
  },

  updateActivityLocal: (activityId, patch) => {
    const id = String(activityId || '');
    if (!id || !patch || typeof patch !== 'object') return;
    set(state => ({
      activities: (state.activities || []).map(activity => (
        getActivityId(activity) === id ? { ...activity, ...patch } : activity
      )),
    }));
  },

  bumpCanvasEpistemicNonce: () => {
    set(state => ({ canvasEpistemicNonce: (state.canvasEpistemicNonce || 0) + 1 }));
  },

  setObjectProcessing: (targets, phase, { runId } = {}) => {
    const keyPhase = String(phase || '').trim();
    const list = normalizeProcessingTargets(targets);
    if (!keyPhase || !list.length) return;
    set(state => {
      const next = { ...(state.objectProcessing || {}) };
      for (const target of list) {
        const prev = next[target.key];
        if (runId != null && prev?.runId != null && prev.runId > runId) continue;
        next[target.key] = {
          phase: keyPhase,
          activityId: target.activityId || prev?.activityId || null,
          shapeId: target.shapeId || prev?.shapeId || null,
          runId: runId ?? prev?.runId ?? null,
        };
      }
      return { objectProcessing: next };
    });
  },

  clearObjectProcessing: (targets, { runId, onlyPhase } = {}) => {
    const list = targets == null
      ? null
      : normalizeProcessingTargets(targets);
    set(state => {
      const prev = state.objectProcessing || {};
      if (!list) {
        return Object.keys(prev).length ? { objectProcessing: {} } : state;
      }
      if (!list.length) return state;
      let changed = false;
      const next = { ...prev };
      for (const target of list) {
        const entry = next[target.key];
        if (!entry) continue;
        if (onlyPhase && entry.phase !== onlyPhase) continue;
        if (runId != null && entry.runId != null && entry.runId !== runId) continue;
        delete next[target.key];
        changed = true;
      }
      return changed ? { objectProcessing: next } : state;
    });
  },

  setPinEpistemicType: async (activityId, epistemicType) => {
    const { hashtag, canEdit, isOwner, updateActivityLocal, activities } = get();
    if (!hashtag || !(canEdit || isOwner)) return null;
    const id = String(activityId || '');
    if (!id) return null;
    const nextType = epistemicType == null || epistemicType === ''
      ? null
      : String(epistemicType).trim().toLowerCase();

    const seq = (pinEpistemicUpdateSeq.get(id) || 0) + 1;
    pinEpistemicUpdateSeq.set(id, seq);

    const prevType = activities.find(a => getActivityId(a) === id)?.epistemicType ?? null;
    updateActivityLocal(id, { epistemicType: nextType });
    try {
      const result = await patchPinEpistemicType(hashtag, id, nextType);
      if (pinEpistemicUpdateSeq.get(id) !== seq) return result;
      updateActivityLocal(id, { epistemicType: result?.epistemicType ?? nextType });
      return result;
    } catch (err) {
      if (pinEpistemicUpdateSeq.get(id) === seq) {
        updateActivityLocal(id, { epistemicType: prevType });
      }
      console.warn('Failed to update pin epistemic type', err);
      throw err;
    }
  },

  removeActivitiesLocal: activityIds => {
    const ids = new Set((activityIds || []).map(String).filter(Boolean));
    if (!ids.size) return;
    set(state => {
      const activities = (state.activities || []).filter(a => !ids.has(getActivityId(a)));
      const placements = (state.board?.placements || []).filter(
        p => !ids.has(String(p.activityId))
      );
      return {
        activities,
        board: state.board ? { ...state.board, placements } : { placements },
      };
    });
  },

  deleteActivities: async activityIds => {
    const ids = [...new Set((activityIds || []).map(String).filter(Boolean))];
    if (!ids.length) return [];
    const pendingIds = ids.filter(isPendingUrlActivityId);
    const persistedIds = ids.filter(id => !isPendingUrlActivityId(id));
    const deleted = [...pendingIds];
    if (pendingIds.length) {
      get().removeActivitiesLocal(pendingIds);
    }
    await Promise.all(
      persistedIds.map(async id => {
        try {
          await deleteActivityById(id);
          deleted.push(id);
        } catch (err) {
          console.warn('Failed to delete activity', id, err);
        }
      })
    );
    const persistedDeleted = deleted.filter(id => !isPendingUrlActivityId(id));
    if (persistedDeleted.length) {
      get().removeActivitiesLocal(persistedDeleted);
      const { board, isOwner } = get();
      if (isOwner && board?.placements) {
        try {
          await get().savePlacements(board.placements);
        } catch (err) {
          console.warn('Failed to persist placements after pin delete', err);
        }
      }
    }
    return deleted;
  },
}));
